import { Button, Select } from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import type { DefaultOptionType } from 'rc-select/lib/Select';
import { useCallback, useMemo, useRef, useState } from 'react';

import './SpecificationIntFloat.scss';

import type { InputStatus } from 'antd/lib/_util/statusUtils';
import { TextInput } from 'src/components/base/controls/Input/TextInput';
import {
  areIntFloatValueAndOptionEqual,
  toCustomIntFloatDefaultType,
  toIntFloatDefaultType,
  toIntFloatOptionValue,
  toIntFloatSingleValue,
  toIntFloatValue,
} from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloat.functions';
import type {
  IntFloatOptionValue,
  IntFloatValue,
  MeasureUnitIntFloatValue,
} from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatField.types';
import {
  SpecificationIntFloatPopup,
  type SpecificationIntFloatPopupRef,
} from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatPopup';
import { cNs, toNumberOrNull } from 'src/helpers';
import {
  type IProductSpecificationFieldReference,
  ProductSpecificationFieldType,
} from 'src/types/reference/IProductSpecificationReference';
import {
  SPECIFICATION_MEASURE_UNIT_MAPPER,
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

function OptionRender({ value }: { value?: string | number }) {
  return `${SPECIFICATION_MEASURE_UNIT_MAPPER[value as SpecificationMeasureUnit]} (${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value as SpecificationMeasureUnit]})`;
}

type SpecificationIntFloatProps = {
  field: IProductSpecificationFieldReference;

  defaultValue?: IntFloatValue;

  onChange?: (selectedValue: IntFloatValue) => void;

  status?: InputStatus;
  disabled?: boolean;

  containerClassName?: string;
  className?: string;
};

export function SpecificationIntFloat({
  field,
  defaultValue,
  onChange,

  status,
  disabled,

  containerClassName,
  className,
}: Readonly<SpecificationIntFloatProps>) {
  const specificationIntFloatPopupRef = useRef<SpecificationIntFloatPopupRef>(null);

  const [selectedMeasureUnit, setSelectedMeasureUnit] = useState<SpecificationMeasureUnit>(
    (defaultValue != null
      ? Array.isArray(defaultValue)
        ? defaultValue[0]?.measureUnit
        : defaultValue.measureUnit
      : null) ??
      field.defaultMeasureUnit ??
      field.allowedMeasureUnits[0] ??
      SpecificationMeasureUnit.NONE
  );

  const [selectedValue, setSelectedValue] = useState<IntFloatValue>(defaultValue ?? (field.multiselect ? [] : null));
  const selectedOptionsValue = useMemo(() => toIntFloatOptionValue(selectedValue), [selectedValue]);
  const selectedSingleValue = useMemo(() => toIntFloatSingleValue(selectedValue), [selectedValue]);

  const [customValues, setCustomValues] = useState<
    Partial<Record<SpecificationMeasureUnit, MeasureUnitIntFloatValue[]>>
  >(() => {
    if (defaultValue == null) {
      return {};
    }

    if (field.multiselect) {
      if (Array.isArray(defaultValue)) {
        // Check that default value is not in options already
        return defaultValue.reduce(
          (acc, curr) => {
            if (
              field.showOptions &&
              field.options?.[curr.measureUnit]?.some((optionItem) => areIntFloatValueAndOptionEqual(curr, optionItem))
            ) {
              return acc;
            }

            acc[curr.measureUnit] = [curr, ...(acc[curr.measureUnit] ?? [])];

            return acc;
          },
          {} as Partial<Record<SpecificationMeasureUnit, MeasureUnitIntFloatValue[]>>
        );
      }

      // Check that default value is not in options already
      if (
        field.showOptions &&
        field.options?.[defaultValue.measureUnit]?.some((optionItem) =>
          areIntFloatValueAndOptionEqual(defaultValue, optionItem)
        )
      ) {
        return {};
      }

      return {
        [defaultValue.measureUnit]: [defaultValue],
      };
    }

    if (Array.isArray(defaultValue)) {
      const itemsNotInOptions = defaultValue.filter(
        (defaultValueItem) =>
          // Check that default value is not in options already
          !field.showOptions ||
          !field.options?.[defaultValueItem.measureUnit]?.some((optionItem) =>
            areIntFloatValueAndOptionEqual(defaultValueItem, optionItem)
          )
      );

      if (itemsNotInOptions.length === 0) {
        return {};
      }

      return {
        [itemsNotInOptions[0].measureUnit]: itemsNotInOptions,
      };
    }

    // Check that default value is not in options already
    if (
      field.showOptions &&
      field.options?.[defaultValue.measureUnit]?.some((optionItem) =>
        areIntFloatValueAndOptionEqual(defaultValue, optionItem)
      )
    ) {
      return {};
    }

    return {
      [defaultValue.measureUnit]: [defaultValue],
    };
  });

  // Forcibly hide dropdown when popup is opened
  const [forceHideDropdown, setForceHideDropdown] = useState<boolean | undefined>(undefined);

  const noMeasureUnitsAllowed =
    field.allowedMeasureUnits.length === 1 && field.allowedMeasureUnits[0] === SpecificationMeasureUnit.NONE;

  const allowedMeasureUnits: SpecificationMeasureUnit[] = useMemo(
    () =>
      noMeasureUnitsAllowed
        ? [SpecificationMeasureUnit.NONE]
        : field.allowedMeasureUnits.length > 0
          ? field.allowedMeasureUnits
          : (Object.keys(SpecificationMeasureUnit) as SpecificationMeasureUnit[]),
    [noMeasureUnitsAllowed, field.allowedMeasureUnits]
  );

  const measureUnitsOptions: DefaultOptionType[] = useMemo(
    () =>
      allowedMeasureUnits.map((measureUnit) => ({
        label: SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[measureUnit],
        value: measureUnit,
      })),
    [allowedMeasureUnits]
  );

  const fieldOptions: DefaultOptionType[] = useMemo(
    () =>
      field.multiselect && !noMeasureUnitsAllowed
        ? // For multiselect show options for all allowed measure units (groupped by measure unit)
          allowedMeasureUnits.map((allowedMeasureUnit) => ({
            label: `${SPECIFICATION_MEASURE_UNIT_MAPPER[allowedMeasureUnit]}`,
            options: [
              // Collect custom value without measure units
              ...(customValues[allowedMeasureUnit]?.map(toCustomIntFloatDefaultType) ?? []),

              // Collect existed field values without measure units
              ...((field.showOptions
                ? field.options?.[allowedMeasureUnit]?.map(toIntFloatDefaultType(allowedMeasureUnit))
                : null) ?? []),
            ],
          }))
        : // For single select show options only for select measure units
          [
            // Collect custom value without measure units
            ...(customValues[selectedMeasureUnit]?.map(toCustomIntFloatDefaultType) ?? []),

            // Collect existed field values without measure units
            ...((field.showOptions
              ? field.options?.[selectedMeasureUnit]?.map(toIntFloatDefaultType(selectedMeasureUnit))
              : null) ?? []),
          ],
    [
      field.multiselect,
      noMeasureUnitsAllowed,
      allowedMeasureUnits,
      selectedMeasureUnit,
      customValues,
      field.showOptions,
      field.options,
    ]
  );

  const handleCustomValueAdd = useCallback(
    (newCustomValues: MeasureUnitIntFloatValue[]) => {
      if (newCustomValues.length === 0) {
        return;
      }

      setCustomValues((prev) => {
        return newCustomValues.reduce(
          (acc, curr) => {
            const measureUnit = typeof curr === 'number' ? SpecificationMeasureUnit.NONE : curr.measureUnit;

            acc[measureUnit] = [curr, ...(acc[measureUnit] ?? [])];

            return acc;
          },
          // Refresh object to cause hook updates
          { ...prev }
        );
      });

      // Automatically select newly added value
      setSelectedValue((prev) => {
        const newSelectedValues = field.multiselect
          ? Array.isArray(prev)
            ? ([...prev, ...newCustomValues] as IntFloatValue)
            : newCustomValues
          : (newCustomValues[0] ?? null);

        // ToDo: bad pattern to do it in setState function
        setTimeout(() => onChange?.(newSelectedValues));

        return newSelectedValues;
      });
    },
    [field.multiselect, onChange]
  );

  const handlePopupClose = useCallback(() => {
    setForceHideDropdown(undefined);
  }, []);

  const handleCustomClick = useCallback(() => {
    setForceHideDropdown(true);

    specificationIntFloatPopupRef.current?.open();
  }, []);

  const handleChange = useCallback(
    (_newValue: IntFloatOptionValue, selectedOptions?: DefaultOptionType | DefaultOptionType[] | null) => {
      const newSelectedValues = toIntFloatValue(selectedOptions);

      setSelectedValue(newSelectedValues);

      onChange?.(newSelectedValues);
    },
    [onChange]
  );

  const handleSingleChange = useCallback(
    (value?: string) => {
      const newSelectedValue = {
        measureUnit: selectedMeasureUnit,
        value: toNumberOrNull(value ?? null) as number,
      };

      setSelectedValue(newSelectedValue);

      onChange?.(newSelectedValue);
    },
    [selectedMeasureUnit, onChange]
  );

  const handleMeasureUnitsChange = useCallback(
    (newMeasureUnit: SpecificationMeasureUnit) => {
      setSelectedMeasureUnit(newMeasureUnit);

      setSelectedValue(field.multiselect ? [] : null);

      onChange?.(field.multiselect ? [] : null);
    },
    [field.multiselect, onChange]
  );

  return (
    <div className={cNs('specification-int-float__container', containerClassName)}>
      {field.name ? <div className="specification-int-float__label">{field.name}</div> : null}
      <div className="specification-int-float__container__wrapper">
        {field.multiselect || field.showOptions ? (
          <SpecificationIntFloatPopup
            ref={specificationIntFloatPopupRef}
            multiselect={field.multiselect ?? false}
            defaultMeasureUnit={
              field.multiselect
                ? (field.defaultMeasureUnit ?? undefined)
                : (selectedMeasureUnit ?? SpecificationMeasureUnit.NONE)
            }
            allowedMeasureUnits={
              noMeasureUnitsAllowed
                ? []
                : field.multiselect
                  ? allowedMeasureUnits
                  : selectedMeasureUnit !== SpecificationMeasureUnit.NONE
                    ? [selectedMeasureUnit]
                    : []
            }
            minValue={
              (field.type === ProductSpecificationFieldType.INT ? field.minIntValue : field.minFloatValue) ?? null
            }
            maxValue={
              (field.type === ProductSpecificationFieldType.INT ? field.maxIntValue : field.maxFloatValue) ?? null
            }
            onAddValue={handleCustomValueAdd}
            onClose={handlePopupClose}
          >
            <Select<IntFloatOptionValue>
              className={cNs('w-100', className)}
              showSearch={true}
              open={forceHideDropdown === true ? false : undefined}
              value={selectedOptionsValue}
              options={fieldOptions}
              onChange={handleChange}
              mode={field.multiselect ? 'multiple' : undefined}
              status={status}
              disabled={disabled}
              dropdownRender={(menu) => (
                <div className="specification-int-float__dropdown__container">
                  {menu}

                  {field.allowCustomValues && (
                    <div className="specification-int-float__add-button__container">
                      <Button type="primary" onClick={handleCustomClick}>
                        Add Custom Value
                      </Button>
                    </div>
                  )}
                </div>
              )}
              optionRender={(option) =>
                option.data.isCustom ? (
                  <div className="specification-int-float__custom-option">{option.label}</div>
                ) : (
                  option.label
                )
              }
            />
          </SpecificationIntFloatPopup>
        ) : (
          <TextInput
            controlClassName={cNs('w-100', className)}
            type="number"
            value={selectedSingleValue}
            status={status}
            onChange={handleSingleChange}
          />
        )}

        {
          // Show measure units dropdown only for single select
          !noMeasureUnitsAllowed && !field.multiselect && (
            <Select
              style={{ minWidth: 75 }}
              value={selectedMeasureUnit}
              options={measureUnitsOptions}
              onChange={handleMeasureUnitsChange}
              status={status}
              disabled={disabled}
              popupMatchSelectWidth={false}
              optionRender={({ value }) => <OptionRender value={value} />}
            />
          )
        }
      </div>
    </div>
  );
}
